import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Award,
  CalendarDays,
  Download,
  FileCheck,
  Globe,
  Handshake,
  Headset,
  Lightbulb,
  Ruler,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../data/company';
import { GLOBAL_PARTNERS, CERTIFICATIONS } from '../data/customers';
import Seo from '../components/common/Seo';
import { routeMeta } from '../routeMeta';

const valueIcons = [Ruler, ShieldCheck, Lightbulb, Headset];

const SectionIntro = ({ index, eyebrow, title, description, align = 'left', titleId }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.35, ease: 'easeOut' }}
    className={`section-intro ${align === 'center' ? 'mx-auto text-center' : ''}`}
  >
    <div className={`mb-5 flex items-center gap-4 ${align === 'center' ? 'justify-center' : ''}`}>
      <span className="font-['JetBrains_Mono'] text-xs font-bold uppercase tracking-[0.28em] text-[#60A5FA]">
        {index}
      </span>
      <span className="h-px w-16 bg-gradient-to-r from-[#60A5FA] to-transparent" />
      <span className="font-['JetBrains_Mono'] text-xs font-bold uppercase tracking-[0.24em] text-slate-400">
        {eyebrow}
      </span>
    </div>
    <h2 id={titleId} className="font-['Manrope'] text-4xl font-extrabold uppercase tracking-tight text-white md:text-5xl">
      {title}
    </h2>
    {description ? (
      <p className={`mt-5 max-w-2xl text-base leading-8 text-slate-300 md:text-lg ${align === 'center' ? 'mx-auto' : ''}`}>
        {description}
      </p>
    ) : null}
  </motion.div>
);

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      <Seo {...routeMeta['/about']} path="/about" />
      <section className="page-hero-shell tech-grid relative overflow-hidden py-16 md:py-24">
        <div className="tech-orb tech-orb--pulse absolute -left-10 top-12 h-40 w-40 bg-[#2563EB]/25" />
        <div className="tech-orb absolute right-10 top-16 h-64 w-64 bg-cyan-400/10" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <div className="mb-6 flex items-center gap-2 text-sm text-slate-400">
                <Link to="/" className="hover:text-white">Home</Link>
                <span>/</span>
                <span className="text-white">About Us</span>
              </div>
              <div className="tech-shimmer mb-6 inline-flex items-center gap-2 bg-[#1E40AF] px-4 py-2 text-sm font-bold uppercase tracking-wider text-white">
                <Award className="h-4 w-4" />
                {COMPANY_INFO.certification}
              </div>
              <h1 className="font-['Manrope'] text-4xl font-extrabold uppercase tracking-tight text-white md:text-6xl">
                Engineering Excellence Since {COMPANY_INFO.established}
              </h1>
              <p className="mt-6 max-w-2xl text-xl leading-relaxed text-slate-300">
                {COMPANY_INFO.tagline}. LEION Engineering is a leading manufacturer of industrial weighing solutions,
                serving businesses across {COMPANY_INFO.stats.countries} countries with precision-engineered products.
              </p>
            </div>
            <div className="tech-panel depth-card relative aspect-[4/3] overflow-hidden border border-white/10 shadow-[0_30px_90px_rgba(2,8,23,0.45)] sm:aspect-[16/10] lg:aspect-[5/4]">
              <div className="absolute -inset-4 bg-[#1E40AF] opacity-20 blur-2xl" />
                <img
                  src="/assets/about-weighbridge-hero.png"
                  alt="Modern truck weighbridge installation at an industrial facility"
                  loading="lazy"
                  className="media-grade relative h-full w-full object-cover"
                />
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(8,21,43,0)_48%,rgba(8,21,43,0.28)_100%)]" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="tech-grid border-y border-white/10 bg-[#08152b] py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-5">
            {[
              { value: COMPANY_INFO.stats.yearsExperience, label: 'Years Experience' },
              { value: COMPANY_INFO.stats.installations, label: 'Installations' },
              { value: COMPANY_INFO.stats.countries, label: 'Countries' },
              { value: COMPANY_INFO.stats.products, label: 'Products' },
              { value: COMPANY_INFO.stats.customers, label: 'Customers' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="tech-panel border border-white/10 bg-white/5 p-6 text-center"
              >
                <div className="font-['JetBrains_Mono'] text-4xl font-extrabold text-[#1E40AF] md:text-5xl">
                  {stat.value}
                </div>
                <div className="mt-2 text-sm uppercase tracking-wider text-slate-300">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-section--values tech-grid bg-[#08152b] py-20 md:py-28" aria-labelledby="values-heading">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16 lg:px-8">
          <div className="lg:pt-8">
            <SectionIntro
              index="01"
              eyebrow="Operating Principles"
              title="What Drives Us"
              titleId="values-heading"
              description="These are the decision filters behind every installation, support call, product improvement, and long-term customer relationship."
            />
            <div className="mt-8 hidden border-l border-[#60A5FA]/30 pl-6 text-sm leading-7 text-slate-400 lg:block">
              Clear principles make the page easier to scan and make the brand feel more deliberate.
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {COMPANY_INFO.values.map((value, index) => (
              (() => {
                const Icon = valueIcons[index] || Award;
                return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ delay: index * 0.06, duration: 0.3 }}
                className="tech-panel depth-card group min-h-[220px] border border-white/10 bg-white/[0.055] p-7 transition-colors duration-200 hover:border-[#60A5FA]/45 hover:bg-white/[0.075]"
              >
                <div className="mb-8 flex items-center justify-between gap-4">
                  <div className="flex h-12 w-12 items-center justify-center border border-[#60A5FA]/30 bg-[#1E40AF]/25 text-[#BFDBFE]">
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="font-['JetBrains_Mono'] text-xs font-bold text-slate-500">
                    0{index + 1}
                  </span>
                </div>
                <h3 className="mb-3 text-2xl font-bold text-white">{value.title}</h3>
                <p className="leading-7 text-slate-300">{value.description}</p>
              </motion.div>
                );
              })()
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-section--timeline tech-grid bg-[#06101f] py-20 md:py-28" aria-labelledby="milestones-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 grid gap-8 border-b border-white/10 pb-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
            <SectionIntro
              index="02"
              eyebrow="Our Journey"
              title="Company Milestones"
              titleId="milestones-heading"
              description="A quick timeline of the moments that moved LEION from a local engineering company to a global weighing-solutions partner."
            />
            <div className="tech-panel border border-white/10 bg-white/[0.045] p-6">
              <div className="flex items-center gap-3 text-[#BFDBFE]">
                <CalendarDays className="h-5 w-5" />
                <span className="font-['JetBrains_Mono'] text-sm font-bold uppercase tracking-[0.2em]">{COMPANY_INFO.milestones[0].year} to {COMPANY_INFO.milestones[COMPANY_INFO.milestones.length - 1].year}</span>
              </div>
              <p className="mt-3 text-sm leading-7 text-slate-300">
                The timeline is grouped as a dedicated story section so visitors can instantly see a new topic has started.
              </p>
            </div>
          </div>

          <div className="relative mx-auto max-w-6xl">
            <div className="absolute bottom-6 left-5 top-6 w-px bg-gradient-to-b from-[#60A5FA] via-[#1E40AF]/60 to-[#60A5FA]/20 md:left-1/2" />

            <div className="grid gap-6 md:gap-8">
              {COMPANY_INFO.milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ delay: index * 0.04, duration: 0.28 }}
                  className="relative grid gap-4 pl-14 md:grid-cols-[1fr_48px_1fr] md:items-center md:gap-6 md:pl-0"
                >
                  <div className={`${index % 2 === 0 ? 'md:col-start-1' : 'md:col-start-3'} md:row-start-1`}>
                    <div className="tech-panel border border-white/10 bg-white/[0.055] p-6 md:p-7">
                      <div className="mb-3 flex items-center justify-between gap-4">
                        <div className="font-['JetBrains_Mono'] text-2xl font-extrabold text-[#60A5FA]">
                          {milestone.year}
                        </div>
                        <span className="font-['JetBrains_Mono'] text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                          Step {index + 1}
                        </span>
                      </div>
                      <p className="text-base font-medium leading-7 text-slate-200">{milestone.event}</p>
                    </div>
                  </div>
                  <div className="absolute left-[13px] top-7 h-4 w-4 rounded-full border-4 border-[#dbeafe] bg-[#1E40AF] shadow-[0_0_0_8px_rgba(30,64,175,0.16)] md:static md:col-start-2 md:row-start-1 md:mx-auto md:h-5 md:w-5" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0B1F3B] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <span className="text-sm font-mono uppercase tracking-widest text-[#60A5FA]">Quality Assurance</span>
            <h2 className="font-['Manrope'] mt-4 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl">
              Our Certifications
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-slate-300">
              LEION Engineering maintains the highest quality standards with internationally recognized certifications.
            </p>
          </motion.div>

          <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2">
            {CERTIFICATIONS.map((cert, index) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="tech-panel depth-card border border-white/20 bg-white/10 p-8 transition-colors hover:bg-white/15"
              >
                <div className="flex items-start gap-4">
                  <div className="rounded bg-[#1E40AF] p-3">
                    <FileCheck className="h-8 w-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white">{cert.name}</h3>
                    <p className="mt-1 text-sm font-mono text-[#60A5FA]">Certificate No: {cert.certificateNumber}</p>
                  </div>
                </div>

                <div className="mt-6 space-y-2 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span>Date Issued:</span>
                    <span className="font-medium text-white">{cert.dateIssued}</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Valid Until:</span>
                    <span className="font-medium text-white">{cert.validUntil}</span>
                  </div>
                </div>

                <p className="mt-4 text-sm leading-relaxed text-slate-300">
                  <span className="font-semibold text-white">Scope:</span> {cert.scope}
                </p>

                {cert.pdfUrl ? (
                  <a href={cert.pdfUrl} target="_blank" rel="noopener noreferrer" className="tech-shimmer mt-6 inline-flex items-center gap-2 bg-[#1E40AF] px-4 py-2 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#2563EB]">
                    <Download className="h-4 w-4" />
                    Download Certificate
                  </a>
                ) : (
                  <div className="mt-6 inline-flex items-center gap-2 bg-white/10 px-4 py-2 text-sm font-bold uppercase tracking-wider text-white">
                    <Download className="h-4 w-4" />
                    Available on Request
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="tech-grid bg-[#08152b] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <span className="text-sm font-mono uppercase tracking-widest text-[#1E40AF]">Our Strength</span>
            <h2 className="font-['Manrope'] mt-4 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl">
              The Team
            </h2>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-3">
            {COMPANY_INFO.team.map((team, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="tech-panel depth-card border border-white/10 bg-white/5 p-8"
              >
                <Users className="mb-4 h-12 w-12 text-[#1E40AF]" />
                <h3 className="mb-2 text-xl font-bold text-white">{team.name}</h3>
                <p className="text-slate-300">{team.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="tech-grid border-y border-white/10 bg-[#08152b] py-16 md:py-24" data-testid="global-partners-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <span className="text-sm font-mono uppercase tracking-widest text-[#1E40AF]">Global Network</span>
            <h2 className="font-['Manrope'] mt-4 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl">
              Global Sales Partners
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-slate-300">
              Our trusted business associates around the world help us deliver world-class weighing solutions globally.
            </p>
          </motion.div>

          <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-1 lg:grid-cols-2">
            {GLOBAL_PARTNERS.map((partner, index) => (
              <motion.div
                key={partner.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="tech-panel depth-card flex flex-col items-center gap-8 border border-white/10 bg-white/5 p-8 shadow-lg md:flex-row"
              >
                <div className="flex-shrink-0">
                  {partner.logo ? (
                    <img src={partner.logo} alt={partner.name} className="h-40 w-40 object-contain" />
                  ) : (
                    <div className="flex h-40 w-40 items-center justify-center border border-white/10 bg-[#0B1F3B]">
                      <Handshake className="h-16 w-16 text-white" />
                    </div>
                  )}
                </div>
                <div className="text-center md:text-left">
                  <div className="mb-3 inline-flex items-center gap-2 bg-[#1E40AF]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#1E40AF]">
                    <Globe className="h-3 w-3" />
                    {partner.role}
                  </div>
                  <h3 className="text-2xl font-bold text-white">{partner.name}</h3>
                  <p className="mt-2 flex items-center justify-center gap-2 text-slate-300 md:justify-start">
                    <Globe className="h-4 w-4 text-[#1E40AF]" />
                    {partner.country}
                  </p>
                  <p className="mt-4 text-sm text-slate-400">
                    Strategic business partner providing sales and support services for LEION Engineering products in {partner.country}.
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="tech-grid bg-[#08152b] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-['Manrope'] text-3xl font-bold uppercase tracking-tight text-white md:text-4xl">
              Ready to Work Together?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
              Let&apos;s discuss how LEION Engineering can provide the perfect weighing solution for your business.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/products">
                <Button className="tech-shimmer rounded-sm bg-[#1E40AF] px-8 py-5 font-bold uppercase tracking-wider text-white shadow-[0_20px_50px_rgba(30,64,175,0.3)] hover:bg-[#2563EB]" data-testid="about-products-btn">
                  View Products
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="rounded-sm border border-[#60A5FA] bg-transparent px-8 py-5 font-bold uppercase tracking-wider text-[#60A5FA] hover:bg-[#60A5FA] hover:text-[#08152b]" data-testid="about-contact-btn">
                  Contact Us
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
